import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDate, formatDateTime } from '@/lib/format'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { StatusBadge } from '@/components/StatusBadge'
import { Pagination } from '@/components/Pagination'
import { EmptyState } from '@/components/EmptyState'
import { StepsEditor } from '@/components/sequences'
import { MissingVariablesNotice } from '@/components/sequences/MissingVariablesNotice'
import { RenderContactSelect } from '@/components/sequences/RenderContactSelect'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertTriangle,
  ArrowLeft,
  Play,
  Pause,
  Users,
  Save,
  Send,
  Loader2,
} from 'lucide-react'
import { isSendBlocked } from '@/lib/sequences'
import { collectMissingVariables } from '@/lib/templateVariables'
import {
  useSequence,
  useUpdateSequence,
  usePauseSequence,
  useResumeSequence,
  useSequenceSteps,
  useCreateStep,
  useUpdateStep,
  useDeleteStep,
  useReorderSteps,
  useSequenceEnrollments,
  useEmailAccounts,
  useSignatures,
} from '@/hooks/useApi'
import { useToast } from '@/hooks/use-toast'
import { useActivateSequenceGuarded } from '@/hooks/useActivateSequenceGuarded'
import { useTestRecipientEmail } from '@/hooks/useTestRecipientEmail'
import { EnrollSegmentDialog } from '@/components/EnrollSegmentDialog'
import type { Contact } from '@/types/api'

const DAYS = [
  { value: 0, key: 'mon' },
  { value: 1, key: 'tue' },
  { value: 2, key: 'wed' },
  { value: 3, key: 'thu' },
  { value: 4, key: 'fri' },
  { value: 5, key: 'sat' },
  { value: 6, key: 'sun' },
] as const

export function SequenceDetail() {
  const { t } = useTranslation(['sequences', 'common'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [enrollmentPage, setEnrollmentPage] = useState(1)
  const [activeTab, setActiveTab] = useState('steps')

  const { data: sequence, isLoading } = useSequence(id)
  const { data: steps, isLoading: loadingSteps } = useSequenceSteps(id)
  const { data: enrollments, isLoading: loadingEnrollments } = useSequenceEnrollments(id, enrollmentPage)
  const { data: emailAccounts } = useEmailAccounts()
  const { data: allSignatures } = useSignatures()

  const { toast } = useToast()
  const [showFullTest, setShowFullTest] = useState(false)
  const [testEmail, setTestEmail] = useTestRecipientEmail()
  const [testSending, setTestSending] = useState(false)
  // null = dati di esempio
  const [testContact, setTestContact] = useState<Contact | null>(null)
  const [testMissing, setTestMissing] = useState<string[]>([])
  const [testResult, setTestResult] = useState<string | null>(null)
  const [testIsError, setTestIsError] = useState(false)
  const [enrollSegmentDialogOpen, setEnrollSegmentDialogOpen] = useState(false)
  const updateSequence = useUpdateSequence()
  const { activate, isPending: isActivating } = useActivateSequenceGuarded()
  const pauseSequence = usePauseSequence()
  const resumeSequence = useResumeSequence()
  const createStep = useCreateStep()
  const updateStep = useUpdateStep()
  const deleteStep = useDeleteStep()
  const reorderSteps = useReorderSteps()

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!sequence) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">{t('detail.notFound')}</h2>
        <Button variant="outline" onClick={() => navigate('/sequences')}>
          {t('detail.backToList')}
        </Button>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/sequences')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common:back')}
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{sequence.name}</h1>
            <StatusBadge status={sequence.status} />
          </div>
          {sequence.description && (
            <p className="text-muted-foreground">{sequence.description}</p>
          )}
        </div>
        {sequence.status === 'active' && (
          <Button variant="outline" onClick={() => setEnrollSegmentDialogOpen(true)}>
            <Users className="h-4 w-4 mr-2" />
            {t('detail.enrollSegment')}
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => { setShowFullTest(true); setTestResult(null); setTestIsError(false); setTestMissing([]) }}
        >
          <Send className="h-4 w-4 mr-2" />
          {t('detail.testSequence')}
        </Button>
        {sequence.status === 'active' ? (
          <Button
            variant="outline"
            onClick={() => pauseSequence.mutate(id!)}
            disabled={pauseSequence.isPending}
          >
            <Pause className="h-4 w-4 mr-2" />
            {t('detail.pause')}
          </Button>
        ) : sequence.status === 'paused' ? (
          <Button
            onClick={() => resumeSequence.mutate(id!)}
            disabled={resumeSequence.isPending}
          >
            <Play className="h-4 w-4 mr-2" />
            {t('detail.resume')}
          </Button>
        ) : (
          <Button
            onClick={() => activate(sequence)}
            disabled={isActivating}
          >
            <Play className="h-4 w-4 mr-2" />
            {t('detail.activate')}
          </Button>
        )}
      </div>

      {/* Sends blocked: active sequence without sender account */}
      {isSendBlocked(sequence) && (
        <div className="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 mb-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
            <div>
              <p className="font-medium text-amber-800">{t('noSender.bannerTitle')}</p>
              <p className="text-sm text-amber-700">{t('noSender.bannerDescription')}</p>
            </div>
          </div>
          {emailAccounts && emailAccounts.length > 0 ? (
            <Button variant="outline" size="sm" onClick={() => setActiveTab('settings')}>
              {t('noSender.selectAccount')}
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => navigate('/settings')}>
              {t('noSender.connectAccount')}
            </Button>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="settings">{t('detail.tabs.settings')}</TabsTrigger>
          <TabsTrigger value="steps">{t('detail.tabs.steps', { count: steps?.length ?? 0 })}</TabsTrigger>
          <TabsTrigger value="enrollments">{t('detail.tabs.enrollments', { count: enrollments?.total ?? 0 })}</TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <SequenceSettings
            sequence={sequence}
            emailAccounts={emailAccounts || []}
            onUpdate={(data) => updateSequence.mutate({ id: id!, data }, { onSuccess: () => toast({ title: t('detail.settingsSaved') }) })}
            isPending={updateSequence.isPending}
          />
        </TabsContent>

        {/* Steps Tab */}
        <TabsContent value="steps">
          <StepsEditor
            sequenceId={id!}
            signatureHtml={
              sequence?.signature_id
                ? allSignatures?.find((s) => s.id === sequence.signature_id)?.body_html
                : allSignatures?.find((s) => s.is_default)?.body_html
            }
            steps={steps || []}
            isLoading={loadingSteps}
            onCreateStep={(data) => createStep.mutate({ sequenceId: id!, data })}
            onUpdateStep={(stepId, data) => updateStep.mutate({ sequenceId: id!, stepId, data })}
            onDeleteStep={(stepId) => deleteStep.mutate({ sequenceId: id!, stepId })}
            onReorderSteps={(stepIds) => reorderSteps.mutate({ sequenceId: id!, stepIds })}
          />
        </TabsContent>

        {/* Enrollments Tab */}
        <TabsContent value="enrollments">
          <Card>
            <CardHeader>
              <CardTitle>{t('detail.enrolledContacts.title')}</CardTitle>
              <CardDescription>
                {t('detail.enrolledContacts.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingEnrollments ? (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : enrollments && enrollments.items.length > 0 ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('detail.enrolledContacts.table.contact')}</TableHead>
                        <TableHead>{t('detail.enrolledContacts.table.status')}</TableHead>
                        <TableHead>{t('detail.enrolledContacts.table.currentStep')}</TableHead>
                        <TableHead>{t('detail.enrolledContacts.table.nextStepAt')}</TableHead>
                        <TableHead>{t('detail.enrolledContacts.table.started')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {enrollments.items.map((enrollment) => (
                        <TableRow key={enrollment.id}>
                          <TableCell>
                            {enrollment.contact ? (
                              <div>
                                <p className="font-medium">{enrollment.contact.full_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {enrollment.contact.email}
                                </p>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">{t('detail.enrolledContacts.unknownContact')}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={enrollment.status} />
                          </TableCell>
                          <TableCell>{enrollment.current_step}</TableCell>
                          <TableCell>
                            {enrollment.next_step_at
                              ? formatDateTime(enrollment.next_step_at)
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {formatDate(enrollment.started_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Pagination
                    page={enrollmentPage}
                    totalPages={enrollments.pages}
                    onPageChange={setEnrollmentPage}
                  />
                </>
              ) : (
                <EmptyState
                  icon={Users}
                  title={t('detail.enrolledContacts.empty.title')}
                  description={t('detail.enrolledContacts.empty.description')}
                  action={{
                    label: t('detail.enrolledContacts.empty.action'),
                    onClick: () => navigate('/contacts'),
                  }}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Full Sequence Test Dialog */}
      <Dialog open={showFullTest} onOpenChange={setShowFullTest}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.fullTest.title')}</DialogTitle>
            <DialogDescription>
              {t('detail.fullTest.description', { count: steps?.length ?? 0 })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <RenderContactSelect value={testContact} onChange={setTestContact} />
            <Input
              type="email"
              placeholder={t('detail.fullTest.emailPlaceholder')}
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !testSending && testEmail && handleFullTestSend()}
            />
            <p className="text-xs text-muted-foreground">{t('detail.fullTest.recipientHint')}</p>
            {testResult && (
              <p className={`text-sm ${testIsError ? 'text-red-500' : 'text-green-600'}`}>
                {testResult}
              </p>
            )}
            <MissingVariablesNotice variables={testMissing} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFullTest(false)}>
              {t('common:close')}
            </Button>
            <Button onClick={handleFullTestSend} disabled={!testEmail || testSending}>
              {testSending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {testSending ? t('detail.fullTest.sending') : t('detail.fullTest.send', { count: steps?.length ?? 0 })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enroll Segment Dialog */}
      <EnrollSegmentDialog
        open={enrollSegmentDialogOpen}
        onClose={() => setEnrollSegmentDialogOpen(false)}
        preselectedSequenceId={id}
      />
    </div>
  )

  async function handleFullTestSend() {
    if (!testEmail || !id) return
    setTestSending(true)
    setTestResult(null)
    setTestIsError(false)
    setTestMissing([])
    try {
      const token = localStorage.getItem('access_token')
      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/sequences/${id}/test-send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          to_email: testEmail,
          contact_id: testContact?.id ?? null,
          apply_enrollment_overrides: true,
        }),
      })
      const data = await res.json()
      if (res.ok) {
        setTestMissing(collectMissingVariables(data.missing_variables))
        setTestResult(
          data.errors?.length
            ? t('detail.fullTest.sentWithErrors', { sent: data.sent, total: data.total, email: testEmail, errors: data.errors.length })
            : t('detail.fullTest.sent', { sent: data.sent, total: data.total, email: testEmail }),
        )
      } else {
        setTestIsError(true)
        setTestResult(t('detail.fullTest.error', { detail: data.detail || t('detail.fullTest.sendFailed') }))
      }
    } catch {
      setTestIsError(true)
      setTestResult(t('detail.fullTest.networkError'))
    } finally {
      setTestSending(false)
    }
  }
}

// Sequence Settings Component
function SequenceSettings({
  sequence,
  emailAccounts,
  onUpdate,
  isPending,
}: {
  sequence: NonNullable<ReturnType<typeof useSequence>['data']>
  emailAccounts: { id: string; email: string; signature_html?: string | null }[]
  onUpdate: (data: Parameters<ReturnType<typeof useUpdateSequence>['mutate']>[0]['data']) => void
  isPending: boolean
}) {
  const [name, setName] = useState(sequence.name)
  const [description, setDescription] = useState(sequence.description || '')
  const [emailAccountId, setEmailAccountId] = useState(sequence.email_account_id || '')
  const [signatureId, setSignatureId] = useState(sequence.signature_id || '')
  const [sendWindowStart, setSendWindowStart] = useState(sequence.send_window_start)
  const [sendWindowEnd, setSendWindowEnd] = useState(sequence.send_window_end)
  const [sendDays, setSendDays] = useState<number[]>(sequence.send_days)
  const [timezone, setTimezone] = useState(sequence.timezone)
  const [quotePreviousEmails, setQuotePreviousEmails] = useState(sequence.quote_previous_emails)
  // Senza il campo nella risposta il tracciamento resta spento: si accende per scelta (#382)
  const [trackOpens, setTrackOpens] = useState(sequence.track_opens ?? false)
  const { t } = useTranslation(['sequences', 'common'])

  const handleSave = () => {
    onUpdate({
      name,
      description: description || undefined,
      email_account_id: emailAccountId || undefined,
      signature_id: signatureId || null,
      send_window_start: sendWindowStart,
      send_window_end: sendWindowEnd,
      send_days: sendDays,
      timezone,
      quote_previous_emails: quotePreviousEmails,
      track_opens: trackOpens,
    })
  }

  const toggleDay = (day: number) => {
    setSendDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort()
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.title')}</CardTitle>
        <CardDescription>{t('settings.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="name">{t('settings.name')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email-account">{t('settings.sendFrom')}</Label>
            <Select value={emailAccountId} onValueChange={setEmailAccountId}>
              <SelectTrigger>
                <SelectValue placeholder={t('settings.selectEmailAccount')} />
              </SelectTrigger>
              <SelectContent>
                {emailAccounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">{t('settings.sequenceDescription')}</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('settings.descriptionPlaceholder')}
          />
        </div>

        <div className="space-y-2">
          <Label>{t('settings.sendWindow')}</Label>
          <div className="flex items-center gap-4">
            <Input
              type="time"
              value={sendWindowStart}
              onChange={(e) => setSendWindowStart(e.target.value)}
              className="w-32"
            />
            <span className="text-muted-foreground">{t('settings.to')}</span>
            <Input
              type="time"
              value={sendWindowEnd}
              onChange={(e) => setSendWindowEnd(e.target.value)}
              className="w-32"
            />
            <Input
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-48"
              placeholder={t('settings.timezone')}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('settings.sendDays')}</Label>
          <div className="flex gap-2">
            {DAYS.map((day) => (
              <Button
                key={day.value}
                type="button"
                variant={sendDays.includes(day.value) ? 'default' : 'outline'}
                size="sm"
                onClick={() => toggleDay(day.value)}
              >
                {t(`settings.days.${day.key}`)}
              </Button>
            ))}
          </div>
        </div>

        <SignatureSelector
          emailAccountId={emailAccountId}
          signatureId={signatureId}
          onChange={setSignatureId}
        />

        <div className="flex items-start gap-3">
          <Switch
            id="quote-previous-emails"
            className="mt-0.5"
            checked={quotePreviousEmails}
            onCheckedChange={setQuotePreviousEmails}
            aria-describedby="quote-previous-emails-hint"
          />
          <div className="space-y-1">
            <Label htmlFor="quote-previous-emails" className="cursor-pointer">
              {t('settings.quotePreviousEmails')}
            </Label>
            <p id="quote-previous-emails-hint" className="text-sm text-muted-foreground">
              {t('settings.quotePreviousEmailsHint')}
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Switch
            id="track-opens"
            className="mt-0.5"
            checked={trackOpens}
            onCheckedChange={setTrackOpens}
            aria-describedby="track-opens-hint"
          />
          <div className="space-y-1">
            <Label htmlFor="track-opens" className="cursor-pointer">
              {t('settings.trackOpens')}
            </Label>
            <p id="track-opens-hint" className="text-sm text-muted-foreground">
              {t('settings.trackOpensHint')}
            </p>
          </div>
        </div>

        <Button onClick={handleSave} disabled={isPending}>
          <Save className="h-4 w-4 mr-2" />
          {isPending ? t('settings.saving') : t('settings.saveChanges')}
        </Button>
      </CardContent>
    </Card>
  )
}

function SignatureSelector({
  emailAccountId,
  signatureId,
  onChange,
}: {
  emailAccountId: string
  signatureId: string
  onChange: (id: string) => void
}) {
  const { data: signatures } = useSignatures()
  const { t } = useTranslation('sequences')

  if (!emailAccountId) return null

  const defaultSig = signatures?.find((s) => s.is_default)
  const selectedSig = signatures?.find((s) => s.id === signatureId)
  const previewHtml = selectedSig?.body_html || (!signatureId && defaultSig?.body_html)

  return (
    <div className="space-y-2">
      <Label>{t('settings.emailSignature')}</Label>
      <Select value={signatureId || '__default__'} onValueChange={(v) => onChange(v === '__default__' ? '' : v)}>
        <SelectTrigger>
          <SelectValue placeholder={defaultSig ? t('settings.defaultNamed', { name: defaultSig.name }) : t('settings.noSignature')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__default__">
            {defaultSig ? t('settings.defaultOption', { name: defaultSig.name }) : t('settings.noSignature')}
          </SelectItem>
          {signatures?.filter((sig) => !sig.is_default).map((sig) => (
            <SelectItem key={sig.id} value={sig.id}>
              {sig.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {previewHtml && (
        <div className="border rounded p-3 text-sm">
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{t('settings.signaturePreview')}</p>
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </div>
      )}
    </div>
  )
}
